import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { CopyButton } from "@/components/CopyButton";
import { ReportErrorButton } from "@/components/ReportError";
import { TransparencySection } from "@/components/TransparencyDisplay";
import { api, API, assetUrl } from "@/lib/api";
import {
  ExternalLink, FileCheck2, Globe, QrCode, Share2, Download, ShieldCheck, EyeOff, Facebook, Twitter, Linkedin, Mail, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

export default function CandidateProfile() {
  const { muniSlug, year, officeSlug, candSlug } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/public/candidates/by-path/${muniSlug}/${year}/${officeSlug}/${candSlug}`)
      .then((r) => {
        setCandidate(r.data);
        return api.get(`/public/candidates/${r.data.id}/share`);
      })
      .then((r) => setShare(r.data))
      .catch(() => setError("Candidate not found or not currently participating."));
  }, [muniSlug, year, officeSlug, candSlug]);

  const downloadQr = async () => {
    try {
      const res = await fetch(`${API}/public/candidates/${candidate.id}/qr`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wyv-${candidate.slug}-qr.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download QR code");
    }
  };

  if (error)
    return (
      <div className="wyv-container py-20">
        <Card className="max-w-lg">
          <CardContent className="p-8">
            <h1 className="font-heading font-semibold text-xl mb-2">Not found</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button asChild variant="outline" className="mt-5">
              <Link to="/candidates">Back to directory</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );

  if (!candidate)
    return (
      <div className="wyv-container py-12">
        <Skeleton className="h-64 max-w-3xl" />
      </div>
    );

  const initials = candidate.public_name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  const links = candidate.links || {};
  const electionOpen = candidate.election.status === "open";

  return (
    <div className="wyv-container py-10 sm:py-14">
      <div className="grid lg:grid-cols-3 gap-8 items-start">
        {/* PROFILE CARD — candidate's own branding allowed inside this card only */}
        <div className="lg:col-span-2 space-y-6">
          <Card data-testid="candidate-profile-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-6">
                {candidate.photo ? (
                  <img
                    src={assetUrl(candidate.photo)}
                    alt={candidate.public_name}
                    className="h-28 w-28 rounded-xl object-cover border border-slate-200"
                  />
                ) : (
                  <div className="h-28 w-28 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-3xl font-heading font-bold text-slate-400">
                    {initials}
                  </div>
                )}
                <div className="min-w-0">
                  <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="candidate-name">
                    {candidate.public_name}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Candidate for {candidate.office.label} · {candidate.municipality.name} ·{" "}
                    {candidate.election.label}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {candidate.office.ward_label && <Badge variant="outline">{candidate.office.ward_label}</Badge>}
                    {candidate.paper_ballot_position === "supports" && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
                        <FileCheck2 className="h-3 w-3 mr-1" /> Supports paper ballots
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                      Participating candidate
                    </Badge>
                    <ReportErrorButton context={`Candidate page — ${candidate.public_name}`}
                                       defaultCategory="incorrect_candidate_info" size="sm" variant="ghost"
                                       className="h-6 px-2 text-[11px] text-muted-foreground" />
                  </div>
                </div>
              </div>

              {candidate.bio && (
                <>
                  <Separator className="my-6" />
                  <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-2">About</h2>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{candidate.bio}</p>
                </>
              )}
              {candidate.why_running && (
                <>
                  <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-2 mt-5">Why I'm running</h2>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{candidate.why_running}</p>
                </>
              )}
              {candidate.paper_ballot_statement && candidate.paper_ballot_position !== "not_stated" && (
                <>
                  <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-2 mt-5">Paper-ballot position</h2>
                  <p className="text-sm text-foreground leading-relaxed">{candidate.paper_ballot_statement}</p>
                </>
              )}

              {(links.website || links.facebook || links.instagram || links.x || links.youtube) && (
                <>
                  <Separator className="my-6" />
                  <div className="flex flex-wrap gap-3">
                    {links.website && (
                      <a href={links.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline" data-testid="candidate-website-link">
                        <Globe className="h-4 w-4" /> Campaign website <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {links.facebook && (
                      <a href={links.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
                        <Facebook className="h-4 w-4" /> Facebook
                      </a>
                    )}
                    {links.x && (
                      <a href={links.x} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
                        <Twitter className="h-4 w-4" /> X
                      </a>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Election Transparency Questions (Phase 11.3) — voluntary, never scored */}
          <TransparencySection slug={candidate.slug} />

          {/* Neutrality note */}
          <Card className="bg-secondary">
            <CardContent className="p-5 flex items-start gap-3">
              <EyeOff className="h-5 w-5 text-accent mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                No live declaration totals are shown for any candidate while voting is open. Candidates never see who
                declared or any voter information. Witness Your Vote is an independent initiative — participating
                candidates do not control the private declaration database or verification process.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* SIDEBAR — neutral WYV tools */}
        <div className="space-y-5">
          <Card className="border-primary/30 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <h2 className="font-heading font-semibold text-base">Witness your vote</h2>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                Already voted for {candidate.public_name}? Create your voluntary, private, hash-verified declaration now.
              </p>
              {!electionOpen && (
                <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2.5 text-xs text-slate-600 mb-3" data-testid="election-not-open-notice">
                  Declarations are not yet open for this election
                  {candidate.election.voting_opens_at ? ` — voting is scheduled to begin ${new Date(candidate.election.voting_opens_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}` : ""}
                  . You can preview the process below.
                </div>
              )}
              <Button asChild className="w-full h-11" data-testid="candidate-witness-vote-button">
                <Link to={`${candidate.path}/declare`}>Witness My Vote</Link>
              </Button>
            </CardContent>
          </Card>

          {share && (
            <>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <QrCode className="h-5 w-5 text-primary" />
                    <h2 className="font-heading font-semibold text-base">QR code</h2>
                  </div>
                  <img
                    src={`${API}/public/candidates/${candidate.id}/qr`}
                    alt={`QR code linking to ${candidate.public_name}'s Witness Your Vote page`}
                    className="w-40 h-40 border border-slate-200 rounded-lg mx-auto"
                    data-testid="candidate-qr-image"
                  />
                  <Button variant="outline" size="sm" className="w-full mt-4" onClick={downloadQr} data-testid="candidate-qr-download">
                    <Download className="h-4 w-4 mr-2" /> Download QR
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Share2 className="h-5 w-5 text-primary" />
                    <h2 className="font-heading font-semibold text-base">Share</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <a href={share.messages.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-slate-200 text-xs font-medium hover:bg-secondary" data-testid="share-facebook">
                      <Facebook className="h-3.5 w-3.5" /> Facebook
                    </a>
                    <a href={share.messages.x} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-slate-200 text-xs font-medium hover:bg-secondary" data-testid="share-x">
                      <Twitter className="h-3.5 w-3.5" /> X
                    </a>
                    <a href={share.messages.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-slate-200 text-xs font-medium hover:bg-secondary" data-testid="share-linkedin">
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                    <a href={`mailto:?subject=${encodeURIComponent(share.messages.email_subject)}&body=${encodeURIComponent(share.messages.email_body)}`} className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-slate-200 text-xs font-medium hover:bg-secondary" data-testid="share-email">
                      <Mail className="h-3.5 w-3.5" /> Email
                    </a>
                    <a href={`sms:?body=${encodeURIComponent(share.messages.sms)}`} className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-slate-200 text-xs font-medium hover:bg-secondary" data-testid="share-sms">
                      <MessageSquare className="h-3.5 w-3.5" /> SMS
                    </a>
                    <CopyButton text={share.profile_url} label="Copy link" testId="share-copy-link" />
                  </div>
                  <Separator className="my-4" />
                  <p className="text-xs font-semibold text-slate-600 mb-2">Ready-made message</p>
                  <p className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed mb-2">
                    {share.messages.candidate_specific}
                  </p>
                  <CopyButton text={share.messages.candidate_specific} label="Copy message" testId="share-copy-message" />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h2 className="font-heading font-semibold text-base mb-2">Campaign website button</h2>
                  <p className="text-xs text-muted-foreground mb-3">
                    Paste this snippet into your campaign website to add a “Witness Your Vote for {candidate.public_name}” button.
                  </p>
                  <pre className="bg-slate-900 text-slate-200 rounded-lg p-3 text-[10px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap break-all" data-testid="button-snippet-code">
                    {share.button_snippet}
                  </pre>
                  <div className="mt-3">
                    <CopyButton text={share.button_snippet} label="Copy snippet" testId="button-snippet-copy" />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
